// The sessions list's search box (APS-196, design "Sessions"): `key:value` terms over the
// filters the API already takes, written into the URL like the chips. A value with spaces is
// quoted: release:"5.2 (812)". Nothing here reaches the API but the parsed values.
import { SESSION_OUTCOMES, type SessionsSearch } from '../routes/search'

/** The terms the box takes, in the order it writes them. */
export const SEARCH_KEYS = [
  'release',
  'device',
  'os',
  'outcome',
  'screen',
  'issue',
  'flag',
] as const
export type SearchKey = (typeof SEARCH_KEYS)[number]

/** The filters the box sets; every other one (app, range, endpoint) it leaves alone. */
export type SessionQuery = Pick<
  SessionsSearch,
  'release' | 'device' | 'os' | 'outcome' | 'screen' | 'issue' | 'flag' | 'variant'
>

export type SearchError =
  | { kind: 'unknown'; key: string }
  | { kind: 'bare'; word: string }
  | { kind: 'value'; key: SearchKey }
  | { kind: 'twice'; key: SearchKey }
  | { kind: 'quote' }

const MAX: Record<Exclude<SearchKey, 'outcome' | 'issue'>, number> = {
  release: 128,
  device: 128,
  os: 64,
  screen: 256,
  flag: 128,
}
const ISSUE_ID = /^[A-Za-z0-9._:-]{1,128}$/

/** Splits the text into terms: `key:value`, `key:"a quoted value"`, or a bare word. */
function terms(text: string): { key?: string; value: string }[] | 'quote' {
  const out: { key?: string; value: string }[] = []
  let i = 0
  while (i < text.length) {
    if (/\s/.test(text[i] as string)) {
      i++
      continue
    }
    const key = /^([A-Za-z_]+):/.exec(text.slice(i))
    if (key) i += key[0].length
    let value = ''
    if (text[i] === '"') {
      const end = text.indexOf('"', i + 1)
      if (end < 0) return 'quote'
      value = text.slice(i + 1, end)
      i = end + 1
    } else {
      const start = i
      while (i < text.length && !/\s/.test(text[i] as string)) i++
      value = text.slice(start, i)
    }
    out.push(key ? { key: (key[1] as string).toLowerCase(), value } : { value })
  }
  return out
}

export function parseSessionQuery(
  text: string,
): { ok: true; query: SessionQuery } | { ok: false; error: SearchError } {
  const split = terms(text)
  if (split === 'quote') return { ok: false, error: { kind: 'quote' } }
  const query: SessionQuery = {}
  const seen = new Set<string>()
  for (const { key, value } of split) {
    if (key === undefined) return { ok: false, error: { kind: 'bare', word: value } }
    if (!(SEARCH_KEYS as readonly string[]).includes(key))
      return { ok: false, error: { kind: 'unknown', key } }
    const k = key as SearchKey
    if (seen.has(k)) return { ok: false, error: { kind: 'twice', key: k } }
    seen.add(k)
    const bad = { ok: false as const, error: { kind: 'value' as const, key: k } }
    if (k === 'outcome') {
      if (!(SESSION_OUTCOMES as readonly string[]).includes(value)) return bad
      query.outcome = value as SessionQuery['outcome']
    } else if (k === 'issue') {
      if (!ISSUE_ID.test(value)) return bad
      query.issue = value
    } else if (k === 'flag') {
      // flag:name, or flag:name=variant (the variant may be empty: flag:name=).
      const at = value.indexOf('=')
      const name = at < 0 ? value : value.slice(0, at)
      if (!name || name.length > MAX.flag || value.length - name.length - 1 > MAX.flag) return bad
      query.flag = name
      if (at >= 0) query.variant = value.slice(at + 1)
    } else {
      if (!value || value.length > MAX[k]) return bad
      query[k] = value
    }
  }
  return { ok: true, query }
}

const quoted = (value: string) => (/[\s"]/.test(value) || value === '' ? `"${value}"` : value)

/**
 * The URL's filters as the box's text, so editing it shows what is applied. `chips: false` leaves
 * out device, OS and flag, which the page shows as chips (APS-400).
 */
export function formatSessionQuery(s: SessionsSearch, opts: { chips?: boolean } = {}): string {
  const chips = opts.chips !== false
  const parts: string[] = []
  if (s.release) parts.push(`release:${quoted(s.release)}`)
  if (chips && s.device) parts.push(`device:${quoted(s.device)}`)
  if (chips && s.os) parts.push(`os:${quoted(s.os)}`)
  if (s.outcome) parts.push(`outcome:${s.outcome}`)
  if (s.screen) parts.push(`screen:${quoted(s.screen)}`)
  if (s.issue) parts.push(`issue:${s.issue}`)
  if (chips && s.flag)
    parts.push(`flag:${quoted(s.variant === undefined ? s.flag : `${s.flag}=${s.variant}`)}`)
  return parts.join(' ')
}
